import { useEffect, useState } from "react";
import { Plus, Trash2, X, MessageCircle, Pencil, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { api, ATEND_STATUSES, fmtDate, formatApiError, ORIGINS, UNITS, waLink } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyForm = { name: "", phone: "", unit: "iatech", origin: "whatsapp", notes: "" };

const statusStyle = {
  novo: "text-nx-yellow border-nx-yellow/50",
  em_atendimento: "text-blue-300 border-blue-500/40",
  aguardando: "text-amber-300 border-amber-500/40",
  convertido: "text-green-400 border-green-500/40",
  encerrado: "text-zinc-400 border-zinc-600",
};

const inputCls =
  "w-full bg-zinc-900/80 border border-zinc-700 focus:border-nx-yellow focus:outline-none rounded-xl px-3.5 py-2.5 text-sm text-white transition-colors";

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export default function Atendimentos() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    const params = filterStatus ? { status: filterStatus } : {};
    api.get("/atendimentos", { params }).then((r) => setItems(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, [filterStatus]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name, phone: a.phone, unit: a.unit, origin: a.origin, notes: a.notes || "" });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const { data } = await api.patch(`/atendimentos/${editing.id}`, form);
        setItems((ls) => ls.map((i) => (i.id === editing.id ? data : i)));
        toast.success("Atendimento atualizado");
      } else {
        const { data } = await api.post("/atendimentos", form);
        setItems((ls) => [data, ...ls]);
        toast.success("Atendimento registrado");
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/atendimentos/${id}`, { status });
      setItems((ls) => ls.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success("Status atualizado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const convert = async (a) => {
    try {
      await api.post(`/atendimentos/${a.id}/convert`);
      setItems((ls) => ls.map((i) => (i.id === a.id ? { ...i, status: "convertido" } : i)));
      toast.success(`${a.name} virou lead no CRM`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/atendimentos/${id}`);
      setItems((ls) => ls.filter((i) => i.id !== id));
      toast.success("Atendimento removido");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const month = today.slice(0, 7);
  const kpis = [
    { label: "Novos hoje", value: items.filter((i) => (i.created_at || "").slice(0, 10) === today && i.status === "novo").length, testid: "atd-kpi-new-today" },
    { label: "Em aberto", value: items.filter((i) => ["novo", "em_atendimento", "aguardando"].includes(i.status)).length, testid: "atd-kpi-open" },
    { label: "Convertidos no mês", value: items.filter((i) => i.status === "convertido" && (i.created_at || "").slice(0, 7) === month).length, testid: "atd-kpi-converted" },
  ];

  return (
    <div className="space-y-6" data-testid="atendimentos-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-nx-yellow">WhatsApp · (62) 98331-9159</p>
          <h1 className="font-syne text-3xl sm:text-4xl font-extrabold mt-2" data-testid="atendimentos-title">Atendimentos</h1>
        </div>
        <button
          onClick={openCreate}
          data-testid="atendimento-add-button"
          className="inline-flex items-center gap-2 bg-nx-yellow text-nx-dark text-sm font-bold px-5 py-2.5 rounded-full hover:bg-nx-electric transition-colors"
        >
          <Plus className="w-4 h-4" /> Registrar Atendimento
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {kpis.map((k) => (
          <div key={k.testid} className="nx-card p-5" data-testid={k.testid}>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{k.label}</p>
            <p className="font-mono text-xl sm:text-2xl font-bold text-nx-yellow mt-2" data-testid={`${k.testid}-value`}>{k.value}</p>
          </div>
        ))}
      </div>

      <div className="nx-card" data-testid="atendimentos-table">
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-zinc-800">
          <h2 className="font-syne text-lg font-bold mr-auto">Fila de atendimento</h2>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={`${inputCls} !w-auto`} data-testid="atd-filter-status">
            <option value="">Todos os status</option>
            {ATEND_STATUSES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                {["Nome", "Telefone", "Unidade", "Origem", "Status", "Data", "Ações"].map((h) => (
                  <th key={h} className="px-5 py-4 text-[11px] font-mono uppercase tracking-widest text-zinc-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-b border-zinc-800/50 last:border-0 hover:bg-zinc-900/40" data-testid={`atd-row-${a.id}`}>
                  <td className="px-5 py-3.5">
                    <p className="font-semibold">{a.name}</p>
                    {a.notes && <p className="text-[11px] text-zinc-500 truncate max-w-[220px]" title={a.notes}>{a.notes}</p>}
                  </td>
                  <td className="px-5 py-3.5 font-mono text-xs text-zinc-400">{a.phone}</td>
                  <td className="px-5 py-3.5 text-zinc-400 text-xs">{UNITS[a.unit]}</td>
                  <td className="px-5 py-3.5 text-zinc-400 text-xs capitalize">{ORIGINS.find((o) => o.id === a.origin)?.label || a.origin}</td>
                  <td className="px-5 py-3.5">
                    <select
                      value={a.status}
                      onChange={(e) => setStatus(a.id, e.target.value)}
                      data-testid={`atd-status-${a.id}`}
                      className={`bg-zinc-800/60 border rounded-lg px-2 py-1.5 text-[11px] font-mono uppercase focus:outline-none focus:border-nx-yellow ${statusStyle[a.status]}`}
                    >
                      {ATEND_STATUSES.map((s) => (
                        <option key={s.id} value={s.id}>{s.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-3.5 font-mono text-xs text-zinc-400">{fmtDate(a.created_at)}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <a
                        href={waLink(a.phone)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Responder no WhatsApp"
                        data-testid={`atd-whats-${a.id}`}
                        className="text-zinc-400 hover:text-[#25d366] transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </a>
                      {!["convertido", "encerrado"].includes(a.status) && (
                        <button onClick={() => convert(a)} title="Converter em Lead" data-testid={`atd-convert-${a.id}`} className="text-zinc-400 hover:text-nx-yellow transition-colors">
                          <ArrowRightLeft className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => openEdit(a)} title="Editar" data-testid={`atd-edit-${a.id}`} className="text-zinc-400 hover:text-white transition-colors">
                        <Pencil className="w-4 h-4" />
                      </button>
                      {user?.role === "admin" && (
                        <button onClick={() => remove(a.id)} title="Excluir" data-testid={`atd-delete-${a.id}`} className="text-zinc-600 hover:text-red-400 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-zinc-500">Nenhum atendimento registrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="atendimento-form-modal">
          <form onSubmit={save} className="glass rounded-3xl p-7 w-full max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-syne text-xl font-bold">{editing ? "Editar Atendimento" : "Registrar Atendimento"}</h2>
              <button type="button" onClick={() => setShowForm(false)} data-testid="atendimento-form-close" className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nome *">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="atd-name-input" />
              </Field>
              <Field label="Telefone (com DDD) *">
                <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="5562983319159" data-testid="atd-phone-input" />
              </Field>
              <Field label="Unidade de interesse">
                <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={inputCls} data-testid="atd-unit-select">
                  {Object.entries(UNITS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </Field>
              <Field label="Origem">
                <select value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} className={inputCls} data-testid="atd-origin-select">
                  {ORIGINS.map((o) => (
                    <option key={o.id} value={o.id}>{o.label}</option>
                  ))}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Resumo da conversa">
                  <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} placeholder="Ex.: pediu orçamento de tráfego pago" data-testid="atd-notes-input" />
                </Field>
              </div>
            </div>
            <button type="submit" data-testid="atd-submit-button" className="mt-6 w-full bg-nx-yellow text-nx-dark font-bold py-3 rounded-xl hover:bg-nx-electric transition-colors">
              {editing ? "Salvar alterações" : "Registrar atendimento"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
